import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Box,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { CloudUpload, Delete as DeleteIcon, Close } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Category } from "../../types";
import { productService } from "../../services/productService";

const schema = yup.object({
    name: yup.string().required("Category name is required"),
    description: yup.string().optional(),
    parent_id: yup.string().optional(),
    display_order: yup.number().min(0, "Must be 0 or greater").optional(),
    image_url: yup.string().url("Must be a valid URL").optional().nullable(),
    is_active: yup.boolean().optional(),
    is_featured: yup.boolean().optional(),
});

interface CategoryFormData {
    name: string;
    description?: string;
    parent_id?: string;
    display_order?: number;
    image_url?: string;
    is_active: boolean;
    is_featured: boolean;
}

interface CategoryFormProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (category: Category) => void;
    category?: Category | null;
    mode: "create" | "edit";
}

export const CategoryForm: React.FC<CategoryFormProps> = ({
    open,
    onClose,
    onSubmit,
    category,
    mode,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [parentCategories, setParentCategories] = useState<Category[]>([]);
    const [imageUploading, setImageUploading] = useState(false);
    const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
    const [stagedFile, setStagedFile] = useState<File | null>(null);
    const [stagedPreviewUrl, setStagedPreviewUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = useForm<CategoryFormData>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            name: "",
            description: "",
            parent_id: "",
            display_order: 0,
            image_url: "",
            is_active: true,
            is_featured: false,
        },
    });

    useEffect(() => {
        if (open) {
            // Load parent categories for the dropdown
            productService
                .getCategories({ active_only: false })
                .then((cats) => {
                    // Exclude the current category (can't be its own parent)
                    const filtered = category
                        ? cats.filter(
                              (c) => c.category_id !== category.category_id,
                          )
                        : cats;
                    setParentCategories(filtered);
                })
                .catch(() => {});

            setStagedFile(null);
            setStagedPreviewUrl(null);
            if (category && mode === "edit") {
                reset({
                    name: category.name,
                    description: category.description || "",
                    parent_id: category.parent_id || "",
                    display_order: category.display_order ?? 0,
                    image_url: category.image_url || "",
                    is_active: category.is_active,
                    is_featured: category.is_featured ?? false,
                });
                setCurrentImageUrl(category.image_url || null);
            } else {
                setCurrentImageUrl(null);
                reset({
                    name: "",
                    description: "",
                    parent_id: "",
                    display_order: 0,
                    image_url: "",
                    is_active: true,
                    is_featured: false,
                });
            }
        }
    }, [open, category, mode, reset]);

    const handleFormSubmit = async (data: CategoryFormData) => {
        try {
            setLoading(true);
            setError("");

            const categoryData = {
                ...data,
                description: data.description || "",
                parent_id: data.parent_id || undefined,
                display_order: data.display_order ?? 0,
                image_url: data.image_url || undefined,
                is_active: data.is_active ?? true,
                is_featured: data.is_featured ?? false,
            };

            let result: Category;
            if (mode === "create") {
                result = await productService.createCategory(categoryData);
                // Upload staged image immediately after creation
                if (stagedFile) {
                    try {
                        setImageUploading(true);
                        result = await productService.uploadCategoryImage(
                            result.category_id,
                            stagedFile,
                        );
                    } catch {
                        // Non-fatal: category was created; image upload failed
                    } finally {
                        setImageUploading(false);
                    }
                }
            } else {
                result = await productService.updateCategory(
                    category!.category_id,
                    categoryData,
                );
            }

            onSubmit(result);
            onClose();
        } catch (err: any) {
            setError(
                err.response?.data?.detail || `Failed to ${mode} category`,
            );
        } finally {
            setLoading(false);
        }
    };

    const handleStagedFileSelect = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setStagedFile(file);
        const url = URL.createObjectURL(file);
        setStagedPreviewUrl(url);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleImageUpload = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0];
        if (!file || !category?.category_id) return;

        try {
            setImageUploading(true);
            setError("");
            const updated = await productService.uploadCategoryImage(
                category.category_id,
                file,
            );
            setCurrentImageUrl(updated.image_url || null);
            setValue("image_url", updated.image_url || "");
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to upload image");
        } finally {
            setImageUploading(false);
            // Reset file input so the same file can be re-selected
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleImageDelete = async () => {
        if (!category?.category_id) return;

        try {
            setImageUploading(true);
            setError("");
            await productService.deleteCategoryImage(category.category_id);
            setCurrentImageUrl(null);
            setValue("image_url", "");
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to delete image");
        } finally {
            setImageUploading(false);
        }
    };

    const handleClose = () => {
        reset();
        setError("");
        setCurrentImageUrl(null);
        if (stagedPreviewUrl) URL.revokeObjectURL(stagedPreviewUrl);
        setStagedFile(null);
        setStagedPreviewUrl(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pr: 1,
                }}
            >
                {mode === "create" ? "Add New Category" : "Edit Category"}
                {isMobile && (
                    <IconButton
                        edge="end"
                        onClick={handleClose}
                        aria-label="close"
                        size="small"
                    >
                        <Close />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" sx={{ mt: 1 }}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                required
                                fullWidth
                                label="Category Name"
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        )}
                    />

                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Description"
                                multiline
                                minRows={4}
                                maxRows={16}
                                error={!!errors.description}
                                helperText={
                                    errors.description?.message ||
                                    "Supports Markdown: **bold**, _italic_, - lists, ## headings, [links](url), and tables."
                                }
                            />
                        )}
                    />

                    {parentCategories.length > 0 && (
                        <Controller
                            name="parent_id"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>
                                        Parent Category (Optional)
                                    </InputLabel>
                                    <Select
                                        {...field}
                                        label="Parent Category (Optional)"
                                    >
                                        <MenuItem value="">
                                            <em>None (Root Category)</em>
                                        </MenuItem>
                                        {parentCategories.map((cat) => (
                                            <MenuItem
                                                key={cat.category_id}
                                                value={cat.category_id}
                                            >
                                                {cat.level
                                                    ? "—".repeat(cat.level) +
                                                      " "
                                                    : ""}
                                                {cat.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    )}

                    <Controller
                        name="display_order"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Display Order"
                                type="number"
                                inputProps={{ min: 0 }}
                                error={!!errors.display_order}
                                helperText={
                                    errors.display_order?.message ||
                                    "Lower numbers appear first"
                                }
                            />
                        )}
                    />

                    {/* Image section — upload UI available in both create and edit modes */}
                    <Box sx={{ mt: 2, mb: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Category Image
                        </Typography>

                        {/* Preview */}
                        {(currentImageUrl || stagedPreviewUrl) ? (
                            <Box
                                sx={{
                                    position: "relative",
                                    display: "inline-block",
                                    mb: 1,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={stagedPreviewUrl || currentImageUrl!}
                                    alt="Category"
                                    sx={{
                                        maxWidth: 200,
                                        maxHeight: 200,
                                        borderRadius: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        objectFit: "cover",
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                        if (mode === "create" || stagedPreviewUrl) {
                                            if (stagedPreviewUrl) URL.revokeObjectURL(stagedPreviewUrl);
                                            setStagedFile(null);
                                            setStagedPreviewUrl(null);
                                        } else {
                                            handleImageDelete();
                                        }
                                    }}
                                    disabled={imageUploading}
                                    sx={{
                                        position: "absolute",
                                        top: -8,
                                        right: -8,
                                        bgcolor: "background.paper",
                                        boxShadow: 1,
                                        "&:hover": {
                                            bgcolor: "error.light",
                                            color: "white",
                                        },
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                                {stagedPreviewUrl && mode === "create" && (
                                    <Typography
                                        variant="caption"
                                        color="primary"
                                        sx={{ display: "block", mt: 0.5 }}
                                    >
                                        Will be uploaded after saving
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                            >
                                No image selected
                            </Typography>
                        )}

                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={
                                    imageUploading ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <CloudUpload />
                                    )
                                }
                                onClick={() => fileInputRef.current?.click()}
                                disabled={imageUploading || loading}
                            >
                                {currentImageUrl || stagedPreviewUrl
                                    ? "Replace Image"
                                    : "Upload Image"}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                                Max 5MB. Will be optimized to 600×600.
                            </Typography>
                        </Box>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={
                                mode === "edit" && category?.category_id
                                    ? handleImageUpload
                                    : handleStagedFileSelect
                            }
                        />
                    </Box>

                    <Box sx={{ display: "flex", gap: 3, mt: 2 }}>
                        <Controller
                            name="is_active"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                        />
                                    }
                                    label="Active"
                                />
                            )}
                        />
                        <Controller
                            name="is_featured"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                            color="warning"
                                        />
                                    }
                                    label="Featured on Homepage"
                                />
                            )}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit(handleFormSubmit)}
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? (
                        <CircularProgress size={24} />
                    ) : mode === "create" ? (
                        "Create"
                    ) : (
                        "Update"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
